import { env } from 'config';
import Keycloak from 'keycloak-js';

const keycloak = new Keycloak({
  realm: env.KEYCLOAK_REALM,
  clientId: env.KEYCLOAK_CLIENT_ID,
  url: env.KEYCLOAK_URL,
});

// Ключи живут здесь, а не в SessionProvider: за токеном обращаются и модули без React,
// а импорт провайдера ради двух строковых констант замкнул бы их с ним в цикл.
export const ACCESS_TOKEN_KEY = 'access_token' as const;
export const REFRESH_TOKEN_KEY = 'refresh_token' as const;

// Копия в localStorage нужна врезкам и запросам в обход общего axios — сам keycloak-js
// хранит токен только в памяти.
const saveTokens = () => {
  if (keycloak.token) localStorage.setItem(ACCESS_TOKEN_KEY, keycloak.token);
  if (keycloak.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, keycloak.refreshToken);
};

// Запас до истечения, при котором токен считается пригодным. Тридцати секунд хватает,
// чтобы запрос успел уйти и вернуться, даже если сеть медленная.
const TOKEN_MIN_VALIDITY = 30;

/**
 * Отдаёт токен, живой ещё как минимум TOKEN_MIN_VALIDITY секунд.
 *
 * Токен живёт заметно меньше рабочего дня, поэтому брать сохранённое значение нельзя:
 * между двумя действиями пользователя вкладка могла провисеть в фоне дольше срока жизни.
 * updateToken не делает ничего, если время ещё есть, и склеивает параллельные вызовы
 * в один запрос к Keycloak — дёргать его перед каждым обращением к API безопасно.
 *
 * force обновляет токен, даже когда срок ещё не вышел: нужно после 401, когда сервер
 * уже отверг токен, который по нашим часам считается годным.
 */
export const ensureFreshToken = async (force = false): Promise<string | null> => {
  if (!keycloak.authenticated) return null;

  try {
    const refreshed = await keycloak.updateToken(force ? -1 : TOKEN_MIN_VALIDITY);
    if (refreshed) saveTokens();
  } catch {
    // Обновление не удалось по одной из двух причин, и различить их можно по токену.
    // Keycloak ответил 400 (сессия отозвана или истекла) — keycloak-js вызвал clearToken,
    // токена больше нет, и он же сам уводит на страницу входа, потому что init шёл
    // с login-required. Вмешиваться не нужно: при живой SSO-сессии пользователь вернётся
    // обратно с новым токеном и даже не заметит, а logout с нашей стороны, наоборот,
    // погасил бы сессию и потребовал пароль на ровном месте.
    // Сеть отвалилась или Keycloak ответил пятисоткой — токен на месте, он ещё может быть
    // годен. Отдаём как есть: разберётся обработчик ответа, а следующая попытка обновления
    // случится по таймеру или при возврате на вкладку.
  }

  return keycloak.token ?? null;
};

export default keycloak;
